import React, { useMemo } from 'react';
import { ComputeSidebar } from 'modules/Compute/Components/ComputeSidebar/ComputeSidebar';
import ComputeCard from 'modules/Compute/Components/ComputeCard/ComputeCard';
import { useRouter } from 'next/router';
import { useCurrentUser } from 'modules/Auth/hooks';
import { clustersApiEffects } from 'api/computeClusters/apiEffects';
import { nodepoolsApiEffects } from 'api/nodepools/apiEffects';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { deploymentsApiEffects } from 'api/deployments/apiEffects';
import { computeDashboardStyles, computePageStyles } from 'modules/Compute/styles';
import { computeDocumentation, computeLinks, LOCAL_RUNNERS_VALUE_PROP_TEXT } from 'modules/Compute/utils';
import { ViewHeader } from 'modules/Compute/View/ViewHeader';
import { useNodepoolCreationModal } from 'modules/Compute/hooks/useNodepoolCreationModal';
import { IconRocket02, IconCloud, IconNodepool, IconRunner } from 'components/Icons';
import { IconConnect } from 'components/Icons/IconConnect';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { runnersApiEffects } from 'api/runners/apiEffects';
import CalloutCard from '../Components/CalloutCard/CalloutCard';

export const ComputeDashboard = () => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const userId = currentUser?.user_id as string;
  const { openNodepoolCreationModal } = useNodepoolCreationModal();
  const { enableLocalRunnersLDFF } = useFeatureFlagsLD();

  const { data: clustersTE, _fetchStatus: fetchStatusClusters } = useQueryTE(
    [queryKeys.ComputeClusters, { userId }],
    clustersApiEffects.listComputeClustersTE(userId),
  );
  const { data: nodepoolsTE, _fetchStatus: fetchStatusNodepools } = useQueryTE(
    [queryKeys.NodePools, { userId }],
    nodepoolsApiEffects.listAllNodepoolsTE(userId),
  );
  const { data: deploymentsE, _fetchStatus: fetchStatusDeployments } = useQueryTE(
    [queryKeys.Deployments, userId],
    deploymentsApiEffects.listDeploymentsTE(userId),
    {
      enabled: !!userId,
    },
  );
  const { data: runnersE, _fetchStatus: fetchStatusRunners } = useQueryTE(
    [queryKeys.Runners, userId],
    runnersApiEffects.listAllRunnersTE({ userId }),
    {
      enabled: !!userId,
    },
  );

  const clusters = useMemo(() => {
    if (clustersTE._tag === 'Left') return [];
    return clustersTE.right;
  }, [clustersTE]);

  const nodepools = useMemo(() => {
    if (nodepoolsTE._tag === 'Left') return [];
    return nodepoolsTE.right;
  }, [nodepoolsTE]);

  const deployments = useMemo(() => {
    if (deploymentsE._tag === 'Left') return [];
    return deploymentsE.right;
  }, [deploymentsE]);

  const runners = useMemo(() => {
    if (runnersE._tag === 'Left') return [];
    return runnersE.right;
  }, [runnersE]);

  const cardsData = useMemo(
    () => [
      {
        id: 'clusters',
        title: 'Clusters',
        icon: IconCloud,
        viewUrl: '/compute/clusters',
        cta: {
          label: 'Create a Cluster',
          action: () => router.push('/compute/create'),
        },
        total: clusters.length,
        isLoading: fetchStatusClusters === 'loading',
      },
      {
        id: 'nodepools',
        title: 'Nodepools',
        icon: IconNodepool,
        viewUrl: '/compute/nodepools',
        cta: {
          label: 'Create a Nodepool',
          action: openNodepoolCreationModal,
        },
        total: nodepools.length,
        isLoading: fetchStatusNodepools === 'loading',
      },
      {
        id: 'deployments',
        title: 'Deployments',
        icon: IconRocket02,
        viewUrl: '/compute/deployments',
        cta: {
          label: 'Deploy a Model',
          action: () => router.push('/compute/deployments/create'),
        },
        total: deployments.length,
        isLoading: fetchStatusDeployments === 'loading',
      },

      ...(enableLocalRunnersLDFF
        ? [
            {
              id: 'runners',
              title: 'Runners',
              icon: IconRunner,
              viewUrl: '/compute/runners',
              cta: {
                label: 'Connect a Local Runner',
                action: () => router.push(computeDocumentation.localRunnersQuickstart),
              },
              total: runners.length,
              isLoading: fetchStatusRunners === 'loading',
            },
          ]
        : []),
    ],
    [
      clusters,
      nodepools,
      deployments,
      runners,
      fetchStatusClusters,
      fetchStatusNodepools,
      fetchStatusDeployments,
      fetchStatusRunners,
      enableLocalRunnersLDFF,
    ],
  );

  const calloutData = useMemo(() => {
    return [
      {
        id: 'runner',
        icon: IconRunner,
        badge: 'New',
        title: 'Connect a Local Runner',
        text: LOCAL_RUNNERS_VALUE_PROP_TEXT,
        cta: {
          label: 'Connect Runner',
          action: () => router.push(computeDocumentation.localRunnersQuickstart),
        },
      },
      {
        id: 'vpc',
        icon: IconConnect,
        badge: 'New',
        title: 'Connect your own cloud',
        text: 'Connect your AWS, GCP, or Oracle infrastructure. Keep your data secure while scaling AI workloads dynamically and cost efficiently.',
        cta: {
          label: 'Contact support',
          action: () => window.open(computeLinks.contactUs, '_blank'),
        },
      },
    ];
  }, []);

  return (
    <div className={computePageStyles.layout}>
      <ComputeSidebar />
      <div className={computePageStyles.wrapper}>
        <ViewHeader
          title="Compute Overview"
          description={
            <>
              Run your models on your own dedicated hardware. Faster responses. More control. Zero cold starts.{' '}
              <a className="link" href={computeDocumentation.computeOverview} target="_blank" rel="noreferrer">
                Check out the docs.
              </a>
            </>
          }
        />
        <section>
          {/* Compute Resources */}
          <div className={computeDashboardStyles.cardsContainer}>
            {cardsData.map((card) => (
              <ComputeCard key={card.id} {...card} />
            ))}
          </div>
          {/* Callouts */}
          {enableLocalRunnersLDFF && (
            <div className={computeDashboardStyles.cardsContainer}>
              {calloutData.map((callout) => (
                <CalloutCard key={callout.id} {...callout} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
